import React from 'react';
import { Button } from 'react-bootstrap';
import { Building, Save } from 'lucide-react';
import { COMPANY_TYPES } from '../../../../constants/companyTypes';
import { ML_EXEMPTION_CATEGORIES, getMlExemptionSuggestion } from '../../../../utils/mlExemptionCategories';

// Company details section — basic info (name/org.nr/VAT status), address and
// contact info. State and save live in the CompanySettings shell and arrive as
// props. Uses the global settings styles from the shell.
const CompanyDetailsSection = ({ companyDetails, setCompanyDetails, saveCompanyDetails }) => (
  <div className="modern-card">
    <div className="modern-card-header">
      <div>
        <h2 className="section-title">
          <Building size={24} />
          Företagsinformation
        </h2>
        <p className="section-subtitle">
          Uppdatera dina företagsuppgifter som visas på fakturor och offerter
        </p>
      </div>
      <div className="action-buttons">
        <Button
          variant="primary"
          className="d-flex align-items-center gap-2"
          onClick={saveCompanyDetails}
        >
          <Save className="me-2" size={16} />
          Spara företagsuppgifter
        </Button>
      </div>
    </div>

    <div className="modern-card-body">
      <div className="company-form-grid">
        {/* Basic Information Section */}
        <div className="form-section-wrapper" id="grunduppgifter">
          <div className="section-header-external">
            <div className="section-icon">
              <Building size={20} />
            </div>
            <div>
              <h3 className="section-heading-external">Grunduppgifter</h3>
              <p className="section-description">Företagets grundläggande information</p>
            </div>
          </div>

          <div className="form-section">
            <div className="form-fields">
              <div className="form-field half-width">
                <label htmlFor="companyName" className="modern-label">
                  Företagsnamn *
                </label>
                <input
                  type="text"
                  className="modern-input"
                  id="companyName"
                  placeholder="Ange företagsnamn"
                  value={companyDetails.companyName}
                  onChange={(e) => setCompanyDetails({ ...companyDetails, companyName: e.target.value })}
                />
              </div>

              <div className="form-field half-width">
                <label htmlFor="companyType" className="modern-label">
                  Företagsform *
                </label>
                <select
                  className="modern-input"
                  id="companyType"
                  value={companyDetails.companyType}
                  onChange={(e) => setCompanyDetails({ ...companyDetails, companyType: e.target.value })}
                >
                  <option value="">Välj företagsform</option>
                  {COMPANY_TYPES.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="form-field half-width">
                <label htmlFor="registrationNumber" className="modern-label">
                  Organisationsnummer *
                </label>
                <input
                  type="text"
                  className="modern-input"
                  id="registrationNumber"
                  placeholder="XXXXXX-XXXX"
                  value={companyDetails.registrationNumber}
                  onChange={(e) => setCompanyDetails({ ...companyDetails, registrationNumber: e.target.value })}
                />
              </div>

              <div className="form-field half-width">
                <label htmlFor="vatNumber" className="modern-label">
                  Momsnummer
                </label>
                {(companyDetails.vatStatus === 'exempt' || companyDetails.vatStatus === 'not_registered') ? (
                  <input
                    type="text"
                    className="modern-input"
                    id="vatNumber"
                    value="n/a — momsbefriad"
                    disabled
                    title="Momsnummer visas inte på fakturor när tenanten är momsbefriad (ML 17 kap)"
                  />
                ) : (
                  <input
                    type="text"
                    className="modern-input"
                    id="vatNumber"
                    placeholder="SE123456789001"
                    value={companyDetails.vatNumber}
                    onChange={(e) => setCompanyDetails({ ...companyDetails, vatNumber: e.target.value })}
                  />
                )}
              </div>

              {/* Session 3 — Momsstatus per ML 10 / 18 kap */}
              <div className="form-field half-width">
                <label htmlFor="vatStatus" className="modern-label">
                  Momsstatus
                </label>
                <select
                  id="vatStatus"
                  className="modern-input"
                  value={companyDetails.vatStatus || 'registered'}
                  onChange={(e) => setCompanyDetails({
                    ...companyDetails,
                    vatStatus: e.target.value,
                    // Clear stale reason when leaving exempt mode
                    vatExemptionReason: e.target.value === 'exempt' ? companyDetails.vatExemptionReason : ''
                  })}
                >
                  <option value="registered">Momsregistrerad (full moms)</option>
                  <option value="exempt">Aktivitetsbefriad (ML 10 kap)</option>
                  <option value="not_registered">Ej momsregistrerad (under tröskel, ML 18 kap)</option>
                </select>
                <small className="text-muted d-block mt-1">
                  Påverkar fakturatemplate och bokföring av in-/utgående moms.
                </small>
              </div>

              {companyDetails.vatStatus === 'exempt' && (
                <>
                  {/* Session 4 — kategori-picker for vat_exemption_reason. Auto-fills the
                      free-text textarea below; editing remains supported (Skatteverket
                      fakturering: chapter-level citation suffices, ML 17 kap). */}
                  <div className="form-field full-width">
                    <label htmlFor="vatExemptionCategory" className="modern-label">
                      Välj kategori (ML 10 kap)
                    </label>
                    <select
                      id="vatExemptionCategory"
                      className="modern-input"
                      // Controlled with `value=""`: React resets the picker to the
                      // placeholder option after each onChange, so the user can pick
                      // a kategori again (or pick a different one) — the textarea
                      // below holds the canonical value.
                      value=""
                      onChange={(e) => {
                        const suggestion = getMlExemptionSuggestion(e.target.value);
                        if (suggestion) {
                          setCompanyDetails({
                            ...companyDetails,
                            vatExemptionReason: suggestion,
                          });
                        }
                      }}
                    >
                      <option value="">— välj en vanlig kategori (eller skriv fritt nedan) —</option>
                      {ML_EXEMPTION_CATEGORIES.map((c) => (
                        <option key={c.id} value={c.id}>{c.label}</option>
                      ))}
                    </select>
                    <small className="text-muted d-block mt-1">
                      Picker fyller textarean nedan. Du kan editera fritt efter val.
                    </small>
                  </div>

                  <div className="form-field full-width">
                    <label htmlFor="vatExemptionReason" className="modern-label">
                      Grund för undantag (ML 10 kap) *
                    </label>
                    <textarea
                      id="vatExemptionReason"
                      className="modern-input"
                      rows={2}
                      placeholder="Ex: Sjukvård enligt mervärdesskattelagen 10 kap"
                      value={companyDetails.vatExemptionReason || ''}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, vatExemptionReason: e.target.value })}
                    />
                    <small className="text-muted d-block mt-1">
                      Texten visas på fakturan (ML 17 kap fakturainnehåll). Krävs vid Aktivitetsbefriad.
                    </small>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Address Section */}
        <div className="form-section-wrapper" id="adress">
          <div className="section-header-external">
            <div className="section-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </div>
            <div>
              <h3 className="section-heading-external">Adressinformation</h3>
              <p className="section-description">Företagets postadress och kontaktuppgifter</p>
            </div>
          </div>

          <div className="form-section">
            <div className="form-fields">
              <div className="form-field full-width">
                <label htmlFor="address" className="modern-label">
                  Gatuadress *
                </label>
                <input
                  type="text"
                  className="modern-input"
                  id="address"
                  placeholder="Ange gatuadress"
                  value={companyDetails.address}
                  onChange={(e) => setCompanyDetails({ ...companyDetails, address: e.target.value })}
                />
              </div>

              <div className="form-field half-width">
                <label htmlFor="postalCode" className="modern-label">
                  Postnummer *
                </label>
                <input
                  type="text"
                  className="modern-input"
                  id="postalCode"
                  placeholder="123 45"
                  value={companyDetails.postalCode}
                  onChange={(e) => setCompanyDetails({ ...companyDetails, postalCode: e.target.value })}
                />
              </div>

              <div className="form-field half-width">
                <label htmlFor="city" className="modern-label">
                  Ort *
                </label>
                <input
                  type="text"
                  className="modern-input"
                  id="city"
                  placeholder="Ange ort"
                  value={companyDetails.city}
                  onChange={(e) => setCompanyDetails({ ...companyDetails, city: e.target.value })}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information Section */}
        <div className="form-section-wrapper" id="kontakt">
          <div className="section-header-external">
            <div className="section-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
            </div>
            <div>
              <h3 className="section-heading-external">Kontaktuppgifter</h3>
              <p className="section-description">Telefon, e-post och kontaktpersoner</p>
            </div>
          </div>

          <div className="form-section">
            <div className="form-fields">
              <div className="form-field half-width">
                <label htmlFor="companyPhone" className="modern-label">
                  Telefonnummer
                </label>
                <input
                  type="tel"
                  className="modern-input"
                  id="companyPhone"
                  placeholder="08-123 456 78"
                  value={companyDetails.companyPhone}
                  onChange={(e) => setCompanyDetails({ ...companyDetails, companyPhone: e.target.value })}
                />
              </div>

              <div className="form-field half-width">
                <label htmlFor="companyEmail" className="modern-label">
                  E-postadress
                </label>
                <input
                  type="email"
                  className="modern-input"
                  id="companyEmail"
                  placeholder="info@företag.se"
                  value={companyDetails.companyEmail}
                  onChange={(e) => setCompanyDetails({ ...companyDetails, companyEmail: e.target.value })}
                />
              </div>

              <div className="form-field half-width">
                <label htmlFor="contactPerson" className="modern-label">
                  Kontaktperson
                </label>
                <input
                  type="text"
                  className="modern-input"
                  id="contactPerson"
                  placeholder="Ange kontaktperson"
                  value={companyDetails.contactPerson}
                  onChange={(e) => setCompanyDetails({ ...companyDetails, contactPerson: e.target.value })}
                />
              </div>

              <div className="form-field half-width">
                <label htmlFor="contactEmail" className="modern-label">
                  Kontakt E-post
                </label>
                <input
                  type="email"
                  className="modern-input"
                  id="contactEmail"
                  placeholder="kontakt@företag.se"
                  value={companyDetails.contactEmail}
                  onChange={(e) => setCompanyDetails({ ...companyDetails, contactEmail: e.target.value })}
                />
              </div>

              <div className="form-field half-width">
                <label htmlFor="contactPhone" className="modern-label">
                  Kontakt Telefon
                </label>
                <input
                  type="tel"
                  className="modern-input"
                  id="contactPhone"
                  placeholder="070-123 45 67"
                  value={companyDetails.contactPhone}
                  onChange={(e) => setCompanyDetails({ ...companyDetails, contactPhone: e.target.value })}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default CompanyDetailsSection;
